import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Global, ThemeProvider } from '@emotion/react';

import { UserProvider } from '../context/user-context';
import { OsanoProvider } from '../context/osano-context';
import globalStyles from '../styles/global-styles';
import { HEADER_BLOCK_TYPENAMES } from '../constants';

import FlexibleBlock from './FlexibleBlock';
import Footer from './footer/Footer';
import FooterSimple from './footer/FooterSimple';
import ChineseFooter from './chinese-footer/Footer';
import Navbar from './navbar/Navbar';
import SkipToMain from './SkipToMain';
import ChineseContactSidebar from './ChineseContactSidebar';
import GlobalBanner from './GlobalBanner';

const Layout = ({
  language,
  pageOverrides,
  navigationType,
  blocksBeforeContent,
  blocksAfterContent,
  freeTrialOverride,
  children
}) => {
  const firstHeaderBlockIndex = blocksBeforeContent?.findIndex(
    (block) => HEADER_BLOCK_TYPENAMES.includes(block.__typename)
  ) ?? -1;

  return (
    <ThemeProvider theme={{ pageOverrides }}>
      <OsanoProvider>
        <UserProvider defaultLanguage={language} freeTrialOverride={freeTrialOverride}>
          <Global styles={globalStyles} />
          <SkipToMain />
          <Navbar type={navigationType} />
          <GlobalBanner />
          <main id="main">
            {blocksBeforeContent && blocksBeforeContent.map((block, index) => (
              <FlexibleBlock
                // Can't assume block id is unique, so need to use index
                // eslint-disable-next-line react/no-array-index-key
                key={`before-${index}`}
                type={block.__typename}
                data={block}
                // This only applies to header blocks that use H1's
                useH1={index === firstHeaderBlockIndex}
              />
            ))}
            {children}
            {blocksAfterContent && blocksAfterContent.map((block, index) => (
              <FlexibleBlock
                // Can't assume block id is unique, so need to use index
                // eslint-disable-next-line react/no-array-index-key
                key={`after-${index}`}
                type={block.__typename}
                data={block}
              />
            ))}
          </main>
          {language === 'zh-Hans' && <ChineseContactSidebar />}
          {navigationType !== 'Simple' && (
            language === 'zh-Hans' ? <ChineseFooter /> : <Footer />
          )}
          {navigationType === 'Simple' && <FooterSimple />}
        </UserProvider>
      </OsanoProvider>
    </ThemeProvider>
  );
};

Layout.propTypes = {
  language: PropTypes.string.isRequired,
  freeTrialOverride: PropTypes.string,
  pageOverrides: PropTypes.arrayOf(PropTypes.string),
  navigationType: PropTypes.string,
  blocksBeforeContent: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      __typename: PropTypes.string.isRequired
    })
  ),
  blocksAfterContent: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      __typename: PropTypes.string.isRequired
    })
  ),
  children: PropTypes.node
};

export const query = graphql`
  fragment LayoutFragment on ContentfulLayout {
    navigationType
    blocksBeforeContent {
      ...FlexibleBlockFragment
    }
    blocksAfterContent {
      ...FlexibleBlockFragment
    }
  }
`;

export default Layout;
